import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { contentApi } from "../api";
import * as Icons from "lucide-react";
import { ArrowUpRight } from "lucide-react";
import type { CSSProperties } from "react";

export default function ViewerHome() {
  const { data, isLoading } = useQuery({ queryKey: ["content"], queryFn: contentApi.get });

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto px-8 py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-48 surface animate-pulse bg-ink-100/40" />
          ))}
        </div>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="max-w-6xl mx-auto px-8">
      {/* Editorial hero */}
      <section className="pt-16 pb-12">
        <h1 className="text-[44px] sm:text-[56px] leading-[1.05] tracking-tight font-medium text-ink-900">
          What do you{" "}
          <span className="display italic font-medium text-accent-500">need</span>{" "}
          today?
        </h1>
        <p className="text-[15px] text-ink-500 mt-3 max-w-xl">
          {data.meta.tagline || "Pick a department, or search across the whole portal."}
        </p>
      </section>

      <div className="border-t border-ink-200/70" />

      {/* Department cards */}
      <section className="py-10">
        <div className="flex items-center justify-between mb-6">
          <h2 className="eyebrow">Departments</h2>
          <span className="text-[12px] text-ink-400">
            {data.departments.length} total
          </span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {data.departments.map((d) => {
            const Icon =
              ((Icons as unknown) as Record<string, Icons.LucideIcon>)[toPascal(d.icon)] ??
              Icons.LayoutGrid;
            const tileCount = d.sections.reduce((acc, s) => acc + s.tiles.length, 0);

            const accentStyle: CSSProperties = {
              ["--accent-color" as never]: d.color,
            };

            return (
              <Link
                key={d.id}
                to={`/d/${d.slug}`}
                style={accentStyle}
                className="group surface accent-top p-6 hover:shadow-soft hover:-translate-y-0.5 transition-all"
              >
                <div className="flex items-start gap-4">
                  <div
                    className="size-11 rounded-xl grid place-items-center shrink-0"
                    style={{
                      backgroundColor: `${d.color}1f`, // ~12% opacity tint
                      color: d.color,
                    }}
                  >
                    <Icon className="size-5" />
                  </div>
                  <div className="flex-1 min-w-0 pt-0.5">
                    <h3 className="display text-[20px] font-medium text-ink-900 leading-snug tracking-tight">
                      {d.name}
                    </h3>
                    <p className="text-[12.5px] text-ink-500 mt-1.5 line-clamp-2 leading-[1.5]">
                      {d.description}
                    </p>
                  </div>
                  <ArrowUpRight className="size-4 text-ink-300 group-hover:text-ink-900 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0" />
                </div>
                <div className="mt-5 flex items-center justify-between">
                  {data.meta.show_tile_count_badges && (
                    <span className="chip">
                      <span className="display italic">{tileCount}</span>{" "}
                      {tileCount === 1 ? "service" : "services"}
                    </span>
                  )}
                  <div className="text-[11px] text-ink-400 font-mono ml-auto">
                    /{d.slug}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>

        {data.departments.length === 0 && (
          <div className="surface p-12 text-center text-ink-500">No departments yet.</div>
        )}
      </section>
    </div>
  );
}

function toPascal(s: string): string {
  return s
    .split(/[-_\s]/)
    .map((p) => (p ? p[0].toUpperCase() + p.slice(1) : ""))
    .join("");
}
